import React from 'react';
import { Document, Page, StyleSheet, View } from '@react-pdf/renderer';
import { NewHeaderSection } from './NewHeaderSection';
import { NewFooterSection } from './NewFooterSection';
import { NewProjectDetailsSection } from './NewProjectDetailsSection';
import { ProjectUsersSection } from './ProjectUsersSection';
import { NewShippingModesSection } from './NewShippingModesSection';
import { TruckLayoutSection } from './TruckLayoutSection';
import { TruckLayoutSectionHeader } from './TruckLayoutSectionHeader';
import { PalletConfigurationHeader } from './PalletConfigurationHeader';
import { PalletConfigurationSection } from './PalletConfigurationSection';
import { LpnSection } from './LpnSection';
import { LpnSectionHeader } from './LpnSectionHeader';
import { PreShipSection } from './PreShipSection';
import { PreShipSectionHeader } from './PreShipSectionHeader';
import { PreshipRQLSection } from './PreshipRQLSection';
import { PreshipRQLSectionHeader } from './PreshipRQLSectionHeader';
import { SynopsisPDF } from './SynopsisPDF';
import { SummaryPDF } from './SummaryPDF';
import { MediaPDF } from './MediaPDF';
import { StandardHeader } from './StandardHeader';

// Optionally register fonts if needed
// Font.register({
//   family: 'Open Sans',
//   fonts: [
//     { src: '/fonts/OpenSans-Regular.ttf' },
//     { src: '/fonts/OpenSans-Bold.ttf', fontWeight: 'bold' },
//   ]
// });

const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        padding: 15,
        paddingBottom: 55,
        fontFamily: 'Helvetica',
    },
    content: {
        flex: 1,
        paddingTop: 6,
        paddingBottom: 40,
    },
    rqlPageContent: {
        flex: 1,
        paddingTop: 0,
        paddingBottom: 30,
    }
});

interface ProjectDefinitionPDFProps {
    data: {
        basicInfo: {
            id: string;
            projectName: string;
            projectDescription: string;
            createdBy: string;
            createdAt: string;
            target?: string | number;
            plusByUser?: string | number;
            percentage?: string | number;
        };
        creator?: {
            id: number;
            name: string;
            email?: string;
            role?: string;
        };
        users?: Array<{
            id: number;
            name: string;
            email?: string;
            role?: string;
        }>;
        shippingModes: Array<{
            id?: number;
            mode: number;
            transitMode: string;
            startCity: string;
            startStateName: string;
            startZip: string;
            endCity: string;
            endStateName: string;
            endZip: string;
            distanceTravelled: string;
            elevationChanges: string;
            elevationMax?: string;
            truckLayout?: any;
            intermediateStopNotes?: string;
        }>;
        pallets: Array<any>;
        synopsisData?: {
            projectName: string;
            projectDescription: string;
            overallDecision: string;
            overallDecisionFailureNote: string;
            validStackingTest: string;
            reasonForInvalidStackingTest: string;
            failureModes: Record<string, Record<string, string>>;
            newFailureModes: Array<{ failureModeName: string; status: string }>;
        };
        palletsForTestStack?: Array<any>;
        summaryData?: {
            preShipLean?: Array<any>;
            postShipLean?: Array<any>;
            unitLoadPosition?: Array<any>;
            preShipFTL?: Array<any>;
            postShipFTL?: Array<any>;
            stackTestLean?: Array<any>;
            stackTestCompression?: Array<any>;
            stackTestLeanByWeek?: Array<{ label: string; data: any }>;
            stackTestHeightByMeasurement?: Array<{ label: string; data: any }>;
        };
    };
    selectedImages?: Array<any>;
    palletTypes?: Array<any>;
    selectedOptions: string[];
}
const determineFailureModeStatus = (additionalStats: any) => {
    if (!additionalStats) return 'dataMissing';

    // Check data fill status first
    if (additionalStats.dataFillStatus === 'dataMissing' || additionalStats.dataFillStatus === 'N/A' || additionalStats.dataFillStatus === 'Incomplete') {
        return 'dataMissing';
    }

    // If there's any data but the overall status is 'Fail', return 'fail'
    if (additionalStats.overallStatus === 'Fail') {
        return 'fail';
    }

    // Otherwise, if the data is complete or incomplete but passes, return 'pass'
    if (additionalStats.overallStatus === 'Pass') {
        return 'pass';
    }

    // If we can't determine, default to data missing
    return 'dataMissing';
};

export const NewProjectDefinitionPDF: React.FC<ProjectDefinitionPDFProps> = ({ data, selectedImages = [], palletTypes = [], selectedOptions }) => {
    // Process the shipping modes to display only a few per page
    console.log('selectedOptions', selectedOptions);
    const MODES_PER_PAGE = 2;
    const shippingModePages = [];
    for (let i = 0; i < data.shippingModes.length; i += MODES_PER_PAGE) {
        shippingModePages.push(data.shippingModes.slice(i, i + MODES_PER_PAGE));
    }

    // Check if we need LPN section (only if at least one pallet has lpnNumbers)
    const hasLpnData = data.pallets.some(pallet =>
        pallet.lpnNumbers && pallet.lpnNumbers.length > 0
    );

    const hasStageData = (variant: 'preShip' | 'postShip' | 'postStack') => data.pallets.some(pallet =>
        pallet.lpnNumbers?.some(lpn =>
            ['leans', 'ftlRows', 'unitLoadPositions', 'unitLoadHeights'].some(field =>
                (lpn[field] || []).some(record => record.variant === variant)
            )
        )
    );
    const hasPreShipData = hasStageData('preShip');
    const hasPostShipData = hasStageData('postShip');
    const hasPostStackData = hasStageData('postStack');

    const palletsWithRQL = (variant: 'preShip' | 'postShip' | 'postStack') => data.pallets.filter(pallet =>
        pallet.rqls && pallet.rqls.some(rql => rql.variant === variant)
    );
    const preShipRQLPallets = palletsWithRQL('preShip');
    const postShipRQLPallets = palletsWithRQL('postShip');
    const postStackRQLPallets = palletsWithRQL('postStack');

    // Check for synopsis and summary data
    const hasSynopsisData = !!data.synopsisData;
    const hasSummaryData = !!data.summaryData;

    // Check if we have selected images
    const hasSelectedImages = selectedImages.length > 0;

    return (
        <Document>
            {/* Project Details and Users Page */}
            <Page size="A4" style={styles.page}>
                <NewHeaderSection projectId={data.basicInfo.id} createdAt={data.basicInfo.createdAt} />
                <View style={styles.content}>
                    <NewProjectDetailsSection projectDetails={data.basicInfo} />
                    {data.creator && (
                        <ProjectUsersSection
                            creatorId={data.basicInfo.id}
                            users={data.users || []}
                        />
                    )}
                    {shippingModePages[0] && (
                        <NewShippingModesSection shippingModes={shippingModePages[0]} />
                    )}
                </View>
                <NewFooterSection />
            </Page>

            {/* Additional Shipping Modes Pages (if needed) */}
            {shippingModePages.slice(1).map((modes, pageIndex) => (
                <Page key={`shipping-page-${pageIndex + 1}`} size="A4" style={styles.page}>
                    <StandardHeader projectId={data.basicInfo.id} createdAt={data.basicInfo.createdAt} title="Shipping Modes" />
                    <View style={styles.content}>
                        <NewShippingModesSection shippingModes={modes} />
                    </View>
                    <NewFooterSection />
                </Page>
            ))}

            {/* Synopsis Page */}
            {hasSynopsisData && (
                <Page size="A4" style={styles.page}>
                    <StandardHeader projectId={data.basicInfo.id} createdAt={data.basicInfo.createdAt} title="Test Synopsis" />
                    <View style={styles.content}>
                        <SynopsisPDF
                            synopsisData={data.synopsisData!}
                            fullData={data}
                            palletsForTestStack={data.palletsForTestStack}
                        />
                    </View>
                    <NewFooterSection />
                </Page>
            )}

            {/* Summary Page */}
            {hasSummaryData && (
                <Page size="A4" style={styles.page}>
                    <StandardHeader projectId={data.basicInfo.id} createdAt={data.basicInfo.createdAt} title="Summary" />
                    <View style={styles.content}>
                        <SummaryPDF
                            preShipLean={data.summaryData?.preShipLean}
                            postShipLean={data.summaryData?.postShipLean}
                            unitLoadPosition={data.summaryData?.unitLoadPosition}
                            preShipFTL={data.summaryData?.preShipFTL}
                            postShipFTL={data.summaryData?.postShipFTL}
                            stackTestLean={data.summaryData?.stackTestLean}
                            stackTestCompression={data.summaryData?.stackTestCompression}
                            stackTestLeanByWeek={data.summaryData?.stackTestLeanByWeek}
                            stackTestHeightByMeasurement={data.summaryData?.stackTestHeightByMeasurement}
                        />
                    </View>
                    <NewFooterSection />
                </Page>
            )}

            {/* Truck Layout Page */}
            {selectedOptions.includes('truckLayout') && data.shippingModes.some(mode => mode.transitMode === 'Truck Air ride' || mode.transitMode === 'Truck leaf spring') && (
                <Page size="A4" style={styles.page}>
                    <StandardHeader projectId={data.basicInfo.id} createdAt={data.basicInfo.createdAt} title="Truck Layout" />
                    <View style={styles.content}>
                        <TruckLayoutSection shippingModes={data.shippingModes} />
                    </View>
                    <NewFooterSection />
                </Page>
            )}

            {/* Pallet Configuration Pages - One page per pallet */}
            {selectedOptions.includes('palletConfig') && data.pallets.map((pallet, palletIndex) => {
                return (
                    <Page
                        key={`pallet-config-${palletIndex}`}
                        size="A4"
                        style={styles.page}
                    >
                        <StandardHeader projectId={data.basicInfo.id} createdAt={data.basicInfo.createdAt} title="Pallet Configuration" />
                        <View style={styles.content}>
                            <PalletConfigurationSection
                                pallet={pallet}
                            />
                        </View>
                        <NewFooterSection />
                    </Page>
                );
            })}

            {/* LPN Section Page */}
            {selectedOptions.includes('lpn') && hasLpnData && (
                <Page size="A4" style={styles.page}>
                    <StandardHeader projectId={data.basicInfo.id} createdAt={data.basicInfo.createdAt} title="Pallet Identifier" />
                    <View style={styles.content}>
                        <LpnSection pallets={data.pallets} />
                    </View>
                    <NewFooterSection />
                </Page>
            )}

            {/* PreShip Section Page */}
            {selectedOptions.includes('preShip') && hasPreShipData && (
                <Page size="A4" style={styles.page}>
                    <StandardHeader projectId={data.basicInfo.id} createdAt={data.basicInfo.createdAt} title="PreShip" />
                    <View style={styles.content}>
                        <PreShipSection
                            pallets={data.pallets}
                            parentVariant='preShip'
                            target={data.basicInfo.target ? Number(data.basicInfo.target) : 2}
                        />
                    </View>
                    <NewFooterSection />
                </Page>
            )}

            {/* PreShip RQL Section Pages - One page per pallet with RQL data */}
            {selectedOptions.includes('preShip') && preShipRQLPallets.map((pallet, index) => {
                // Only include pallets with preShip RQLs
                const hasPreShipRQL = pallet.rqls && pallet.rqls.some(rql => rql.variant === 'preShip');
                if (!hasPreShipRQL) return null;

                return (
                    <Page
                        key={`preship-rql-${pallet.id}-page`}
                        size="A4"
                        style={styles.page}
                        wrap={false}
                    >
                        <StandardHeader projectId={data.basicInfo.id} createdAt={data.basicInfo.createdAt} title="PreShip RQL" />
                        <View style={styles.rqlPageContent}>
                            <PreshipRQLSection pallets={[pallet]} variant="preShip" />
                        </View>
                        <NewFooterSection />
                    </Page>
                );
            })}
     {/* PostShip Section Page */}
     {selectedOptions.includes('postShip') && hasPostShipData && (
                <Page size="A4" style={styles.page}>
                    <StandardHeader projectId={data.basicInfo.id} createdAt={data.basicInfo.createdAt} title="PostShip" />
                    <View style={styles.content}>
                        <PreShipSection
                            pallets={data.pallets}
                            parentVariant='postShip'
                            target={data.basicInfo.target ? Number(data.basicInfo.target) : 2}
                        />
                    </View>
                    <NewFooterSection />
                </Page>
            )}

            {/* PreShip RQL Section Pages - One page per pallet with RQL data */}
            {selectedOptions.includes('postShip') && postShipRQLPallets.map((pallet, index) => {
                // Only include pallets with preShip RQLs
                const hasPreShipRQL = pallet.rqls && pallet.rqls.some(rql => rql.variant === 'postShip');
                if (!hasPreShipRQL) return null;

                return (
                    <Page
                        key={`postship-rql-${pallet.id}-page`}
                        size="A4"
                        style={styles.page}
                        wrap={false}
                    >
                        <StandardHeader projectId={data.basicInfo.id} createdAt={data.basicInfo.createdAt} title="PostShip RQL" />
                        <View style={styles.rqlPageContent}>
                            <PreshipRQLSection pallets={[pallet]} variant="postShip" />
                        </View>
                        <NewFooterSection />
                    </Page>
                );
            })}
               {/* PostStack Section Page */}
               {selectedOptions.includes('postStack') && hasPostStackData && (
                <Page size="A4" style={styles.page}>
                    <StandardHeader projectId={data.basicInfo.id} createdAt={data.basicInfo.createdAt} title="PostStack" />
                    <View style={styles.content}>
                        <PreShipSection
                            pallets={data.pallets}
                            parentVariant='postStack'
                            target={data.basicInfo.target ? Number(data.basicInfo.target) : 2}
                        />
                    </View>
                    <NewFooterSection />
                </Page>
            )}

            {/* PostStack RQL Section Pages - One page per pallet with RQL data */}
            {selectedOptions.includes('postStack') && postStackRQLPallets.map((pallet, index) => {
                // Only include pallets with preShip RQLs
                const hasPreShipRQL = pallet.rqls && pallet.rqls.some(rql => rql.variant === 'postStack');
                if (!hasPreShipRQL) return null;

                return (
                    <Page
                        key={`poststack-rql-${pallet.id}-page`}
                        size="A4"
                        style={styles.page}
                        wrap={false}
                    >
                        <StandardHeader projectId={data.basicInfo.id} createdAt={data.basicInfo.createdAt} title="PostStack RQL" />
                        <View style={styles.rqlPageContent}>
                            <PreshipRQLSection pallets={[pallet]} variant="postStack" />
                        </View>
                        <NewFooterSection />
                    </Page>
                );
            })}
            {/* Media Images Page - Add after synopsis and summary pages */}
            {hasSelectedImages && (
                <Page size="A4" style={styles.page}>
                    <StandardHeader projectId={data.basicInfo.id} createdAt={data.basicInfo.createdAt} title="Media" />
                    <View style={styles.content}>
                        <MediaPDF
                            selectedImages={selectedImages}
                            palletTypes={palletTypes}
                        />
                    </View>
                    <NewFooterSection />
                </Page>
            )}
        </Document>
    );
};
